import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "ui/components/card";
import { BARCHART } from "icons";
import { Icon } from "components";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="rounded border bg-white p-2 text-xs shadow">
        <div className="font-medium mb-1">{data.department}</div>
        <div
          className={data.difference >= 0 ? "text-green-600" : "text-red-600"}
        >
          Difference: {data.difference > 0 ? "+" : ""}
          {data.difference.toFixed(2)}%
        </div>
        <div className="mt-1 text-muted-foreground">
          <div>Logged: {data.loggedHours} h</div>
          <div>Available: {data.payrollHours} h</div>
        </div>
      </div>
    );
  }

  return null;
};

export default function LoggedVsPayrollDifferenceChart({
  data = [],
  loading = false,
  isError = false,
  error = null,
}) {
  const chartData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((d) => {
      const payroll = Number(d.payrollHours) || 0;
      const logged = Number(d.loggedHours) || Number(d.x) || 0;
      // Calculate percentage difference relative to payroll
      // (logged - payroll) / payroll * 100
      const difference = payroll > 0 ? ((logged - payroll) / payroll) * 100 : 0;

      return {
        department: d.department.name || "Unknown",
        payrollHours: payroll,
        loggedHours: logged,
        difference,
      };
    });
  }, [data]);

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="h-[500px] flex items-center justify-center">
          <div className="text-sm text-muted-foreground">
            Loading chart data...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="w-full">
        <CardContent className="h-[500px] flex items-center justify-center">
          <div className="text-sm text-red-500">
            {error?.message || "Failed to fetch chart data"}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Card className="w-full mt-8">
      <CardHeader className="flex flex-row items-center justify-between !p-4 border-b bg-white">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Icon icon={BARCHART} className="size-4" />
          <div className="text-muted-foreground">
            Logged vs Available Difference
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="h-[500px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="department"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{
                  value: "Difference (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                cursor={{ fill: "transparent" }}
                content={<CustomTooltip />}
              />
              <ReferenceLine y={0} stroke="#000" />
              <Bar dataKey="difference" fill="#8884d8">
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.difference >= 0 ? "#16a34a" : "#dc2626"} // green-600 : red-600
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
